"use client";

import type { AdminTopSearchQuery, AnalyticsPageStatsType } from "@/types";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import PeopleIcon from "@mui/icons-material/People";
import TimerIcon from "@mui/icons-material/Timer";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { calculateRelativeTime, formatDuration } from "~/lib/formatDate";
import AnalyticsPageStatItem from "./AnalyticsPageStatItem";

interface AnalyticsPageStatsProps {
  stats: AnalyticsPageStatsType;
  topSearches?: AdminTopSearchQuery[];
  periodStart: Date;
}

export default function AnalyticsPageStats({ stats, topSearches, periodStart }: AnalyticsPageStatsProps) {
  return (
    <Box sx={{ display: "flex", gap: 2, mt: 1, pl: { xs: 1, md: 0 }, alignItems: "center" }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1 }}>
        {/* Period stats row */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              width: "28px"
            }}>
            {calculateRelativeTime(periodStart, { disablePrefix: true })}
          </Typography>

          <AnalyticsPageStatItem
            tooltipTitle={`Vizitatori unici (${calculateRelativeTime(periodStart, { disablePrefix: true })})`}
            icon={<PeopleIcon />}
            value={(stats.visitors ?? 0).toLocaleString("ro-RO")}
            comparison={stats.comparison?.visitors}
          />

          <AnalyticsPageStatItem
            tooltipTitle={`Vizualizări totale (${calculateRelativeTime(periodStart, { disablePrefix: true })})`}
            icon={<VisibilityIcon />}
            value={(stats.pageviews ?? 0).toLocaleString("ro-RO")}
            comparison={stats.comparison?.pageviews}
          />

          <AnalyticsPageStatItem
            tooltipTitle={`Bounce-uri (${calculateRelativeTime(periodStart, { disablePrefix: true })})`}
            icon={<ExitToAppIcon />}
            value={(stats.bounces ?? 0).toLocaleString("ro-RO")}
            comparison={stats.comparison?.bounces}
            positiveIfHigher={false}
          />

          <AnalyticsPageStatItem
            tooltipTitle={`Timp mediu pe pagină (${calculateRelativeTime(periodStart, { disablePrefix: true })})`}
            icon={<TimerIcon />}
            value={formatDuration(stats.avgDurationSeconds ?? 0)}
            comparison={stats.comparison?.avgDurationSeconds}
          />
        </Box>

        {/* All-time stats row (no comparisons) */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              width: "28px"
            }}>
            total
          </Typography>

          <AnalyticsPageStatItem
            tooltipTitle="Vizitatori unici (total)"
            icon={<PeopleIcon />}
            value={(stats.alltime?.visitors ?? 0).toLocaleString("ro-RO")}
          />

          <AnalyticsPageStatItem
            tooltipTitle="Vizualizări totale (total)"
            icon={<VisibilityIcon />}
            value={(stats.alltime?.pageviews ?? 0).toLocaleString("ro-RO")}
          />

          <AnalyticsPageStatItem
            tooltipTitle="Bounce-uri (total)"
            icon={<ExitToAppIcon />}
            value={(stats.alltime?.bounces ?? 0).toLocaleString("ro-RO")}
          />

          <AnalyticsPageStatItem
            tooltipTitle="Timp mediu pe pagină (total)"
            icon={<TimerIcon />}
            value={formatDuration(stats.alltime?.avgDurationSeconds ?? 0)}
          />
        </Box>

        {/* Referrers row (all-time breakdown) */}
        {stats.referrers && stats.referrers.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                width: "28px",
                mt: 0.5
              }}>
              refer
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1 }}>
              {(() => {
                const referrers = stats.referrers;
                const max = Math.max(...referrers.map((r) => r.count), 1);
                return referrers.slice(0, 6).map((r) => (
                  <Box key={r.domain} sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                    <Avatar
                      src={`https://icons.duckduckgo.com/ip3/${r.domain}.ico`}
                      alt={r.domain}
                      sx={{ width: 20, height: 20 }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: "text.primary", width: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                      {r.domain}
                    </Typography>
                    <Box sx={{ flex: 1, height: 8, backgroundColor: "background.paper", borderRadius: 1, overflow: "hidden" }}>
                      <Box sx={{ height: "100%", width: `${Math.round((r.count / max) * 100)}%`, backgroundColor: "primary.main" }} />
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        ml: 1,
                        minWidth: 56,
                        textAlign: "right"
                      }}>
                      {r.count.toLocaleString("ro-RO")}
                    </Typography>
                  </Box>
                ));
              })()}
            </Box>
          </Box>
        )}

        {topSearches && topSearches.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap", mt: 1, pl: { xs: 1, md: 0 } }}>
            <Typography variant="caption" sx={{
              color: "text.secondary"
            }}>
              căutări
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, flex: 1 }}>
              {topSearches.slice(0, 6).map((q) => (
                <Box key={q.query} sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.primary",
                      minWidth: 160,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap"
                    }}>
                    {q.query}
                  </Typography>
                  <Typography variant="caption" sx={{
                    color: "text.secondary"
                  }}>
                    {q.clicks} clicks
                  </Typography>
                  <Typography variant="caption" sx={{
                    color: "text.secondary"
                  }}>
                    {q.impressions} imp.
                  </Typography>
                  <Typography variant="caption" sx={{
                    color: "text.secondary"
                  }}>
                    poz. {q.position.toFixed(1)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
